import { UerChapter, UerClassifier, workInSumEnumItems } from 'constantsProject';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { UerRecordStatusEnum } from 'gql/graphql';

import { useEffect, useMemo } from 'react';

import { priceListUerRowWithRatioGql } from '../createOrEditUerQueries';

interface IUseGetUer {
    uerId?: string;
    reset: (data: Record<string, any>) => void;
}

export const useGetUer = (props: IUseGetUer) => {
    const { uerId, reset } = props;

    const variables = useMemo(() => ({ uerBaseRowId: Number(uerId) }), [uerId]);

    const [uerRowData, uerRowDataStatus] = useRequest({
        requestId: 'UerRow',
        query: priceListUerRowWithRatioGql,
        variables,
        withEffect: Boolean(uerId),
    });

    useEffect(() => {
        if (uerRowData?.uerPriceListUerRowWithRatio?.data) {
            const { chapter, uerClassifier, currentPrices, status, workKind } =
                uerRowData.uerPriceListUerRowWithRatio.data;

            let chapterValue = null;

            if (chapter) {
                chapterValue = {
                    id: chapter,
                    label: UerChapter[chapter],
                };
            } else if ([UerRecordStatusEnum.Draft, UerRecordStatusEnum.NewRecord].includes(status)) {
                chapterValue = { id: 'CHAPTER1_7', label: '1-7' };
            }

            const prices = currentPrices.reduce((acc: any, current) => {
                acc[current.constructionTypeId] = current;

                return acc;
            }, {});

            const workKindItem = workKind?.workUniqueId
                ? { id: workKind?.workUniqueId, label: workKind?.workName }
                : undefined;
            const workInSumItem = workKind?.workInSum
                ? workInSumEnumItems.find((i) => i.id === workKind?.workInSum)
                : undefined;

            const { workCode, workUnit, workRatio } = workKind;

            reset({
                ...uerRowData?.uerPriceListUerRowWithRatio?.data,
                uerClassifier: uerClassifier
                    ? {
                          id: uerClassifier,
                          label: UerClassifier[uerClassifier],
                      }
                    : null,
                chapter: chapterValue,
                workKind: workKindItem,
                workInSum: workInSumItem,
                workCode,
                workUnit,
                workRatio,
                ...prices,
            });
        }
    }, [uerRowData, reset]);

    const [resetUerRowHandler] = useResetStoreData({ requestId: 'UerRow' });

    const ratioItemsUsedIds = useMemo(
        () =>
            (uerRowData?.uerPriceListUerRowWithRatio?.data?.ratioItems || [])
                .filter((item) => item.isUsedLink)
                .map((item) => item.rationId),
        [uerRowData?.uerPriceListUerRowWithRatio?.data?.ratioItems],
    );

    return [uerRowData, uerRowDataStatus, resetUerRowHandler, ratioItemsUsedIds] as const;
};
